/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { If, Then, Else, When } from "react-if";
import { useLocation, Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Box, Tag, Heading, Breadcrumbs, theme } from "@nordcloud/gnui";
import { trimText, isNotNil } from "~/tools";

const normalize = (value: string | undefined) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

type TagsProps = {
  color: string;
  text: string;
};

type ComponentLabel = {
  value: string;
  label: string;
};

type ComponentLabelProps = {
  label: string;
  uri: string;
};

type BreadcrumbsBoxProps = {
  title?: string;
  tags?: TagsProps[];
  children?: ReactNode;
  labels?: ComponentLabel[];
  customBreadcrumbs?: ComponentLabelProps[];
};

export function BreadcrumbsBox({
  title,
  children,
  tags = [],
  labels,
  customBreadcrumbs,
}: BreadcrumbsBoxProps) {
  const location = useLocation();
  const path: string[] = location.pathname.split("/");

  const START_PAGE_LABEL = "Home";
  const defaultLabelMaxLength = 25;
  const itemLabelMaxLength = 30;

  const uniquePath = [...new Set(path)];
  const displayTitle = title ?? uniquePath.at(-1);
  const breadcrumbs = uniquePath.map((item: string, index: number) => {
    const label =
      item === ""
        ? trimText(START_PAGE_LABEL, defaultLabelMaxLength)
        : trimText(
            labels?.find((element) => element.value === item)?.label || item,
            itemLabelMaxLength
          );
    const uri = `${uniquePath.slice(0, index).join("/")}/${item}`;

    return { label, uri };
  });

  // TODO: add modifiers in GNUI and remove styles
  const inlineFlexStyles = { display: "inline-flex", placeItems: "center" };
  const flexStyles = { display: "flex", placeItems: "center" };

  return (
    <Box spacing="spacing04" data-testid="breadcrumbs-box">
      <Row style={flexStyles}>
        <Col>
          <div style={inlineFlexStyles}>
            <Heading level={2} css={{ wordBreak: "break-all" }}>
              <If condition={title}>
                <Then>{displayTitle || ""}</Then>
                <Else>{normalize(displayTitle)}</Else>
              </If>
            </Heading>
            {tags?.map((tag) => (
              <Tag
                key={uuidv4()}
                css={{ margin: "0 0 0 1rem" }}
                color={tag.color}
                text={tag.text}
              />
            ))}
          </div>
          <Breadcrumbs
            list={customBreadcrumbs ? customBreadcrumbs : breadcrumbs}
            renderCustom={({ breadcrumb }) =>
              breadcrumb.isDisabled ? (
                <span
                  css={{
                    pointerEvents: "none",
                    color: `${theme.color.text.text02}`,
                    fontSize: `${theme.fontSizes.sm}`,
                  }}
                >
                  {breadcrumb.label}
                </span>
              ) : (
                <Link to={breadcrumb.uri}>{breadcrumb.label}</Link>
              )
            }
          />
        </Col>
        <When condition={isNotNil(children)}>
          <Col style={{ textAlign: "right" }}>
            <div style={{ display: "flex", marginLeft: "auto" }}>
              {children}
            </div>
          </Col>
        </When>
      </Row>
    </Box>
  );
}
